import React, { useEffect, useMemo, useState, useCallback } from "react";
import { UploadNotices } from "../components/uploads/FileUploadSource";
import { listItems as listUploads } from "../lib/uploadsApi";
import { LifecycleAlert } from "../lib/lifecycleApi.jsx";
import {
  Sparkles, RefreshCcw, AlertTriangle, Wallet, ArrowRightLeft, ShoppingCart,
  TrendingUp, Activity, Clock3, Loader2, Database, Cpu,
} from "lucide-react";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import RecommendationCard from "../components/RecommendationCard";
import SimulateDrawer from "../components/SimulateDrawer";
import { useRole } from "../lib/role.jsx";
import {
  morningBrief, listRecommendations, runNow, ENGINES,
} from "../lib/enginesApi";
import { formatNumber, cn } from "../lib/utils";
import { OriginScope, OriginSummary, OriginBadge, originFromDataMode } from "../lib/origin.jsx";
import { useInventoryValuation } from "../lib/valuation";
import { EndingInventorySnapshot, DemandOutlookTile } from "../components/valuation/BriefTiles";
import ClassChangeAlerts from "../components/classification/ClassChangeAlerts";
import OpportunityPanel from "../components/brief/OpportunityPanel";

// -----------------------------------------------------------------------------
// Morning Brief — the daily operating cycle landing page.
// Shows:
//   1. Hero "Today's Opportunity" tile with freshness/engine badges.
//   2. Four sub-tiles (Excess, Deferrable PO, Redistribution, A-class risk).
//   3. Engine-count strip (7 engines, click to filter the top-5 list).
//   4. Top-5 ranked recommendations (RecommendationCard) with Simulate hook.
// Data: /api/engines/brief + /api/engines/recommendations.
// -----------------------------------------------------------------------------

const ENGINE_ICON = {
  demand_forecast:   TrendingUp,
  stockout_adjusted: Activity,
  inventory_health:  Wallet,
  stockout_risk:     AlertTriangle,
  replenishment:     ShoppingCart,
  redistribution:    ArrowRightLeft,
  aging:             Clock3,
};
const ENGINE_ACCENT = Object.fromEntries(ENGINES.map(e => [e.id, e.accent]));
const ENGINE_LABEL  = Object.fromEntries(ENGINES.map(e => [e.id, e.label]));

export default function MorningBrief() {
  return (
    <OriginScope>
      <MorningBriefBody />
    </OriginScope>
  );
}

function engineOriginProps(mode, latest) {
  const origin = originFromDataMode(mode);
  const basis = origin === "actual" ? "1Rotary ingested history" : origin === "mixed" ? "1Rotary + sample history" : "synthetic sample history";
  return { origin, source: `Seven-engine daily cycle · ${basis}`, updatedAt: latest?.started_at };
}

function MorningBriefBody() {
  const { user, branchScope } = useRole();
  const branchFilter = branchScope?.branchId || null;

  const [brief, setBrief] = useState(null);
  const [filtered, setFiltered] = useState([]);
  const [engineFilter, setEngineFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [simRec, setSimRec] = useState(null);
  const [explain, setExplain] = useState(null);
  const closeExplain = useCallback(() => setExplain(null), []);

  const load = useCallback(async () => {
    try {
      setError(null);
      const b = await morningBrief(branchFilter);
      setBrief(b);
      setFiltered(b.top5 || []);
      setEngineFilter(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [branchFilter]);

  useEffect(() => { load(); }, [load]);

  async function onRunCycle() {
    if (running) return;
    setRunning(true);
    try {
      await runNow(user?.employeeId || "ui");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  }

  async function selectEngine(engineId) {
    if (!brief) return;
    if (engineFilter === engineId) {
      setEngineFilter(null);
      setFiltered(brief.top5 || []);
      return;
    }
    setEngineFilter(engineId);
    try {
      const { recommendations } = await listRecommendations({
        engine: engineId, branch_id: branchFilter, limit: 20,
      });
      const pending = recommendations.filter(r => (r.approval?.state || "pending") === "pending");
      pending.sort((a, b) => (b.expected_value_php || 0) - (a.expected_value_php || 0));
      setFiltered(pending.slice(0, 5));
    } catch (e) {
      setError(e.message);
    }
  }

  function onRecChange(updated) {
    setFiltered(list => list.map(r => r.id === updated.id ? updated : r));
    setBrief(b => b ? { ...b, top5: (b.top5 || []).map(r => r.id === updated.id ? updated : r) } : b);
  }

  const latest = brief?.latest_cycle;
  const totalRecs = useMemo(() => {
    const by = brief?.engines_by_count || {};
    return Object.values(by).reduce((s, n) => s + n, 0);
  }, [brief]);
  const dataModeHint = useMemo(() => {
    const engines = latest?.engines || {};
    const modes = new Set(Object.values(engines).map(e => e?.data_mode).filter(Boolean));
    if (modes.has("real_s3_bronze")) return "real_s3_bronze";
    if (modes.has("hybrid")) return "hybrid";
    return "simulated";
  }, [latest]);
  const engineOrigin = engineOriginProps(dataModeHint, latest);
  const valuation = useInventoryValuation(branchFilter);

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Daily operating cycle · AI Layer"
        title="Morning Brief"
        description={branchScope
          ? `Top-5 ranked AI actions for ${branchScope.branchName}, scored across the seven inventory engines.`
          : "Top-5 ranked AI actions across the seven inventory engines — scored by Peso impact and confidence."}
        action={
          <div className="flex items-center gap-2" data-testid="brief-header-actions">
            <CycleBadge latest={latest} />
            <button data-testid="brief-run-now" onClick={onRunCycle} disabled={running || loading}
                    className="px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1.5 disabled:opacity-40">
              {running ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
              {running ? "Running…" : "Run cycle now"}
            </button>
          </div>
        }
      />

      {user?.role === "it_head" && <LifecycleAlert isItHead />}
      {user?.role === "it_head" && <BriefUploadNotices />}
      <OriginSummary />

      {/* Hero */}
      <div className="grid grid-cols-12 gap-4">
        <HeroTile brief={brief} loading={loading} totalRecs={totalRecs} origin={engineOrigin}
                  onExplain={() => setExplain({ nonce: Date.now() })} />
      </div>

      {/* Sub-tiles */}
      <div className="grid grid-cols-12 gap-4">
        <SubTile testid="tile-excess" origin={engineOrigin} label="Excess requiring action"
                 value={brief?.tiles?.excess_requiring_action_php || 0}
                 engine="inventory_health" icon={Wallet} accent="amber"
                 onClick={() => selectEngine("inventory_health")} active={engineFilter === "inventory_health"} />
        <SubTile testid="tile-deferrable" origin={engineOrigin} label="Deferrable purchases"
                 value={brief?.tiles?.deferrable_purchases_php || 0}
                 engine="replenishment" icon={ShoppingCart} accent="primary"
                 onClick={() => selectEngine("replenishment")} active={engineFilter === "replenishment"} />
        <SubTile testid="tile-redistribution" origin={engineOrigin} label="Redistribution opportunity"
                 value={brief?.tiles?.redistribution_opportunity_php || 0}
                 engine="redistribution" icon={ArrowRightLeft} accent="teal"
                 onClick={() => selectEngine("redistribution")} active={engineFilter === "redistribution"} />
        <SubTile testid="tile-a-class-risk" origin={engineOrigin} label="A-class stockout risk"
                 value={brief?.tiles?.a_class_stockout_risk_php || 0}
                 engine="stockout_risk" icon={AlertTriangle} accent="critical"
                 onClick={() => selectEngine("stockout_risk")} active={engineFilter === "stockout_risk"} />
      </div>

      {/* Inventory & demand snapshot */}
      <div className="grid grid-cols-12 gap-4">
        <EndingInventorySnapshot valuation={valuation} />
        <DemandOutlookTile branchId={branchFilter} />
      </div>

      {/* Engine strip */}
      <Tile testid="engine-strip" title="Seven inventory engines" origin={engineOrigin}
            action={engineFilter && (
              <button data-testid="engine-strip-clear" onClick={() => selectEngine(engineFilter)}
                      className="text-[11px] text-muted hover:text-primary">Clear filter ×</button>
            )}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {ENGINES.map(e => {
            const count = brief?.engines_by_count?.[e.id] || 0;
            const Icon = ENGINE_ICON[e.id];
            const active = engineFilter === e.id;
            return (
              <button key={e.id} data-testid={`engine-chip-${e.id}`}
                      onClick={() => selectEngine(e.id)}
                      className={cn("text-left border rounded-sm px-3 py-2.5 transition-colors",
                        active ? "border-primary bg-primary/10 text-primary"
                               : "border-border bg-surface-2/40 hover:border-primary/60")}>
                <div className="flex items-center gap-2">
                  <Icon size={13} className="shrink-0" />
                  <span className="text-[10px] uppercase tracking-widest font-semibold truncate">{e.label}</span>
                </div>
                <div className="font-mono-tabular text-sm font-semibold mt-1" data-testid={`engine-chip-${e.id}-count`}>
                  {count}
                </div>
                <div className="text-[10px] text-muted mt-0.5">pending</div>
              </button>
            );
          })}
        </div>
      </Tile>

      {/* Top-5 list */}
      <Tile testid="top5-tile" origin={engineOrigin}
            title={engineFilter ? `Top 5 · ${ENGINE_LABEL[engineFilter]}` : "Top 5 recommendations · ranked by Peso impact"}
            action={
              <Badge status="info" testid="top5-count">{filtered.length} shown</Badge>
            }>
        {loading ? (
          <SkeletonList />
        ) : error ? (
          <ErrorBanner message={error} onRetry={load} />
        ) : filtered.length === 0 ? (
          <EmptyState engineFilter={engineFilter} onClear={() => selectEngine(engineFilter)} />
        ) : (
          <div className="space-y-2">
            {filtered.map(r => (
              <RecommendationCard key={r.id} rec={decorate(r)}
                                  actor={user?.employeeId || "ui"}
                                  onChange={onRecChange}
                                  onSimulate={setSimRec} />
            ))}
          </div>
        )}
      </Tile>

      {/* Overnight ABC × XYZ class changes — network-wide, so not shown to branch-scoped roles */}
      {!branchScope && <ClassChangeAlerts />}

      <SimulateDrawer open={!!simRec} rec={simRec ? decorate(simRec) : null} onClose={() => setSimRec(null)} />
      <OpportunityPanel req={explain} onClose={closeExplain} origin={engineOrigin}
                        onFresh={() => setExplain({ nonce: Date.now(), fresh: true })} />
    </div>
  );
}

// Decorate the server rec with UI-only metadata (engine label + accent).
function decorate(r) {
  return {
    ...r,
    engine_label: r.engine_label || ENGINE_LABEL[r.engine] || r.engine,
    engine_accent: r.engine_accent || ENGINE_ACCENT[r.engine] || "primary",
  };
}

function HeroTile({ brief, loading, totalRecs, origin, onExplain }) {
  const value = brief?.hero_opportunity_php || 0;
  return (
    <div data-testid="hero-opportunity"
         className="col-span-12 tile p-5 bg-gradient-to-br from-surface via-surface to-surface-2/40 border border-primary/30">
      <div className="flex flex-wrap sm:flex-nowrap items-start gap-4">
        <div className="w-11 h-11 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center text-primary">
          <Sparkles size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">Today's Opportunity</div>
          <div className="font-mono-tabular text-4xl md:text-5xl font-semibold text-primary mt-1 leading-none"
               data-testid="hero-opportunity-value">
            {loading ? "…" : `₱${formatNumber(value, { compact: true })}`}
          </div>
          <div className="mt-2 flex items-center flex-wrap gap-2 text-[11px]">
            <OriginBadge {...origin} testid="hero-data-mode" />
            <Badge status="neutral"><Cpu size={10} className="mr-1" />{totalRecs} total actions</Badge>
            <span className="text-muted">— unlocked across the 7 inventory engines; largest action: &nbsp;
              <strong className="text-fg">
                {brief?.top5?.[0] ? `₱${formatNumber(brief.top5[0].expected_value_php || 0, { compact: true })}` : "—"}
              </strong>
            </span>
          </div>
        </div>
        <button type="button" data-testid="hero-explain-btn" onClick={onExplain} disabled={loading || !value}
                title="Explain what makes up Today's Opportunity"
                className="w-full sm:w-auto shrink-0 self-center inline-flex items-center justify-center gap-1.5 h-10 px-4 border border-primary/40 bg-primary/10 text-primary rounded-sm text-[12.5px] font-semibold hover:bg-primary/15 active:scale-[0.97] transition-[background-color,transform] disabled:opacity-40">
          <Sparkles size={13} /> Explain
        </button>
      </div>
    </div>
  );
}

function SubTile({ label, value, engine, icon: Icon, accent, onClick, active, testid, origin }) {
  const toneCls = active ? "border-primary bg-primary/10" : "border-border bg-surface-2/40 hover:border-primary/60";
  return (
    <div role="button" tabIndex={0} data-testid={testid} onClick={onClick}
         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
         className={cn("col-span-6 md:col-span-3 lg:col-span-3 tile p-3 text-left transition-colors cursor-pointer", toneCls)}>
      <div className="flex items-center gap-2 text-muted">
        <Icon size={13} className={cn("shrink-0",
          accent === "amber" && "text-warning",
          accent === "primary" && "text-primary",
          accent === "teal" && "text-success",
          accent === "critical" && "text-critical")} />
        <span className="text-[10px] uppercase tracking-widest font-semibold truncate">{label}</span>
        <OriginBadge {...origin} className="ml-auto" testid={`${testid}-origin`} />
      </div>
      <div className="font-mono-tabular text-xl font-semibold mt-1.5" data-testid={`${testid}-value`}>
        ₱{formatNumber(value, { compact: true })}
      </div>
      <div className="text-[10px] text-muted mt-0.5">Tap to focus</div>
    </div>
  );
}

function CycleBadge({ latest }) {
  if (!latest || !latest.run_id) {
    return <Badge status="warning" testid="brief-cycle-badge">No cycle yet</Badge>;
  }
  const started = (latest.started_at || "").slice(0, 16).replace("T", " ");
  const ok = (latest.total_recommendations || 0) > 0;
  return (
    <div className="inline-flex items-center gap-2 text-[11px] text-muted" data-testid="brief-cycle-badge">
      <StatusDot status={ok ? "success" : "warning"} />
      <span>Last cycle · {started} UTC</span>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2" data-testid="brief-skeleton">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="border rounded-sm border-border bg-surface-2/40 p-4 animate-pulse">
          <div className="h-3 bg-surface-2 rounded w-1/3 mb-2" />
          <div className="h-4 bg-surface-2 rounded w-2/3" />
        </div>
      ))}
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div data-testid="brief-error" className="border border-critical/40 bg-critical/5 text-critical rounded-sm p-4">
      <div className="font-semibold text-[12px] mb-1">Could not load recommendations</div>
      <div className="text-[11.5px]">{message}</div>
      <button onClick={onRetry} className="mt-2 text-[11px] underline hover:no-underline">Retry →</button>
    </div>
  );
}

function EmptyState({ engineFilter, onClear }) {
  return (
    <div data-testid="brief-empty" className="text-center py-10 text-muted">
      <Sparkles size={22} className="mx-auto mb-2 opacity-60" />
      <div className="text-[13px] font-medium text-fg">
        {engineFilter ? "No pending actions for this engine." : "No pending actions right now."}
      </div>
      <div className="text-[11.5px] mt-1">
        {engineFilter
          ? "Clear the filter to see the network-wide top five."
          : "Click \"Run cycle now\" to score a fresh day."}
      </div>
      {engineFilter && (
        <button onClick={onClear} className="mt-2 text-[11px] text-primary underline">Clear filter</button>
      )}
    </div>
  );
}

function BriefUploadNotices() {
  const [n, setN] = useState([]);
  useEffect(() => { listUploads().then(d => setN((d.notices || []).filter(x => Date.now() - new Date(x.at) < 7 * 864e5))).catch(() => {}); }, []);
  return <UploadNotices notices={n} />;
}
